/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button, Skeleton, Tag } from '../../components/console/ui';
import { useAuthStore } from '../../store/authStore';
import { profileService, preferencesService } from '../../services/api';
import toast from 'react-hot-toast';

type Currency = 'INR' | 'USD';

interface ProfileData {
  id: string;
  email: string;
  name: string;
  phone?: string;
  phone_verified?: boolean;
  phone_verified_at?: string;
  timezone: string;
  profile_photo_url?: string;
  created_at: string;
  updated_at: string;
}

interface Country {
  code: string;
  name: string;
  dialCode: string;
  format: string;
  maxLength: number;
}

// Comprehensive list of all countries with phone formats
const countries: Country[] = [
  { code: 'AF', name: 'Afghanistan', dialCode: '+93', format: '## ### ####', maxLength: 9 },
  { code: 'AL', name: 'Albania', dialCode: '+355', format: '## ### ####', maxLength: 9 },
  { code: 'DZ', name: 'Algeria', dialCode: '+213', format: '### ## ## ##', maxLength: 9 },
  { code: 'AD', name: 'Andorra', dialCode: '+376', format: '### ###', maxLength: 6 },
  { code: 'AO', name: 'Angola', dialCode: '+244', format: '### ### ###', maxLength: 9 },
  { code: 'AG', name: 'Antigua and Barbuda', dialCode: '+1268', format: '### ####', maxLength: 7 },
  { code: 'AR', name: 'Argentina', dialCode: '+54', format: '## #### ####', maxLength: 10 },
  { code: 'AM', name: 'Armenia', dialCode: '+374', format: '## ### ###', maxLength: 8 },
  { code: 'AU', name: 'Australia', dialCode: '+61', format: '### ### ###', maxLength: 9 },
  { code: 'AT', name: 'Austria', dialCode: '+43', format: '### ### ####', maxLength: 10 },
  { code: 'AZ', name: 'Azerbaijan', dialCode: '+994', format: '## ### ## ##', maxLength: 9 },
  { code: 'BS', name: 'Bahamas', dialCode: '+1242', format: '### ####', maxLength: 7 },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', format: '#### ####', maxLength: 8 },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', format: '#### ######', maxLength: 10 },
  { code: 'BB', name: 'Barbados', dialCode: '+1246', format: '### ####', maxLength: 7 },
  { code: 'BY', name: 'Belarus', dialCode: '+375', format: '## ### ## ##', maxLength: 9 },
  { code: 'BE', name: 'Belgium', dialCode: '+32', format: '### ## ## ##', maxLength: 9 },
  { code: 'BZ', name: 'Belize', dialCode: '+501', format: '### ####', maxLength: 7 },
  { code: 'BJ', name: 'Benin', dialCode: '+229', format: '## ## ## ##', maxLength: 8 },
  { code: 'BT', name: 'Bhutan', dialCode: '+975', format: '## ### ###', maxLength: 8 },
  { code: 'BO', name: 'Bolivia', dialCode: '+591', format: '# ### ####', maxLength: 8 },
  { code: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387', format: '## ### ###', maxLength: 8 },
  { code: 'BW', name: 'Botswana', dialCode: '+267', format: '## ### ###', maxLength: 8 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', format: '## ##### ####', maxLength: 11 },
  { code: 'BN', name: 'Brunei', dialCode: '+673', format: '### ####', maxLength: 7 },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359', format: '### ### ###', maxLength: 9 },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226', format: '## ## ## ##', maxLength: 8 },
  { code: 'BI', name: 'Burundi', dialCode: '+257', format: '## ## ## ##', maxLength: 8 },
  { code: 'KH', name: 'Cambodia', dialCode: '+855', format: '## ### ###', maxLength: 8 },
  { code: 'CM', name: 'Cameroon', dialCode: '+237', format: '# ## ## ## ##', maxLength: 9 },
  { code: 'CA', name: 'Canada', dialCode: '+1', format: '### ### ####', maxLength: 10 },
  { code: 'CV', name: 'Cape Verde', dialCode: '+238', format: '### ## ##', maxLength: 7 },
  { code: 'CF', name: 'Central African Republic', dialCode: '+236', format: '## ## ## ##', maxLength: 8 },
  { code: 'TD', name: 'Chad', dialCode: '+235', format: '## ## ## ##', maxLength: 8 },
  { code: 'CL', name: 'Chile', dialCode: '+56', format: '# #### ####', maxLength: 9 },
  { code: 'CN', name: 'China', dialCode: '+86', format: '### #### ####', maxLength: 11 },
  { code: 'CO', name: 'Colombia', dialCode: '+57', format: '### ### ####', maxLength: 10 },
  { code: 'KM', name: 'Comoros', dialCode: '+269', format: '### ## ##', maxLength: 7 },
  { code: 'CG', name: 'Congo', dialCode: '+242', format: '## ### ####', maxLength: 9 },
  { code: 'CD', name: 'Congo (DRC)', dialCode: '+243', format: '## ### ####', maxLength: 9 },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506', format: '#### ####', maxLength: 8 },
  { code: 'CI', name: "Cote d'Ivoire", dialCode: '+225', format: '## ## ## ## ##', maxLength: 10 },
  { code: 'HR', name: 'Croatia', dialCode: '+385', format: '## ### ####', maxLength: 9 },
  { code: 'CU', name: 'Cuba', dialCode: '+53', format: '# ### ####', maxLength: 8 },
  { code: 'CY', name: 'Cyprus', dialCode: '+357', format: '## ### ###', maxLength: 8 },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', format: '### ### ###', maxLength: 9 },
  { code: 'DK', name: 'Denmark', dialCode: '+45', format: '## ## ## ##', maxLength: 8 },
  { code: 'DJ', name: 'Djibouti', dialCode: '+253', format: '## ## ## ##', maxLength: 8 },
  { code: 'DM', name: 'Dominica', dialCode: '+1767', format: '### ####', maxLength: 7 },
  { code: 'DO', name: 'Dominican Republic', dialCode: '+1809', format: '### ####', maxLength: 7 },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', format: '## ### ####', maxLength: 9 },
  { code: 'EG', name: 'Egypt', dialCode: '+20', format: '### ### ####', maxLength: 10 },
  { code: 'SV', name: 'El Salvador', dialCode: '+503', format: '#### ####', maxLength: 8 },
  { code: 'GQ', name: 'Equatorial Guinea', dialCode: '+240', format: '### ### ###', maxLength: 9 },
  { code: 'ER', name: 'Eritrea', dialCode: '+291', format: '# ### ###', maxLength: 7 },
  { code: 'EE', name: 'Estonia', dialCode: '+372', format: '#### ####', maxLength: 8 },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268', format: '## ## ####', maxLength: 8 },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', format: '## ### ####', maxLength: 9 },
  { code: 'FJ', name: 'Fiji', dialCode: '+679', format: '### ####', maxLength: 7 },
  { code: 'FI', name: 'Finland', dialCode: '+358', format: '## ### ## ##', maxLength: 9 },
  { code: 'FR', name: 'France', dialCode: '+33', format: '# ## ## ## ##', maxLength: 9 },
  { code: 'GA', name: 'Gabon', dialCode: '+241', format: '# ## ## ##', maxLength: 7 },
  { code: 'GM', name: 'Gambia', dialCode: '+220', format: '### ####', maxLength: 7 },
  { code: 'GE', name: 'Georgia', dialCode: '+995', format: '### ### ###', maxLength: 9 },
  { code: 'DE', name: 'Germany', dialCode: '+49', format: '### #######', maxLength: 10 },
  { code: 'GH', name: 'Ghana', dialCode: '+233', format: '## ### ####', maxLength: 9 },
  { code: 'GR', name: 'Greece', dialCode: '+30', format: '### ### ####', maxLength: 10 },
  { code: 'GD', name: 'Grenada', dialCode: '+1473', format: '### ####', maxLength: 7 },
  { code: 'GT', name: 'Guatemala', dialCode: '+502', format: '#### ####', maxLength: 8 },
  { code: 'GN', name: 'Guinea', dialCode: '+224', format: '### ## ## ##', maxLength: 9 },
  { code: 'GW', name: 'Guinea-Bissau', dialCode: '+245', format: '# ######', maxLength: 7 },
  { code: 'GY', name: 'Guyana', dialCode: '+592', format: '### ####', maxLength: 7 },
  { code: 'HT', name: 'Haiti', dialCode: '+509', format: '## ## ####', maxLength: 8 },
  { code: 'HN', name: 'Honduras', dialCode: '+504', format: '#### ####', maxLength: 8 },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852', format: '#### ####', maxLength: 8 },
  { code: 'HU', name: 'Hungary', dialCode: '+36', format: '## ### ####', maxLength: 9 },
  { code: 'IS', name: 'Iceland', dialCode: '+354', format: '### ####', maxLength: 7 },
  { code: 'IN', name: 'India', dialCode: '+91', format: '##### #####', maxLength: 10 },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', format: '### ### ####', maxLength: 10 },
  { code: 'IR', name: 'Iran', dialCode: '+98', format: '### ### ####', maxLength: 10 },
  { code: 'IQ', name: 'Iraq', dialCode: '+964', format: '### ### ####', maxLength: 10 },
  { code: 'IE', name: 'Ireland', dialCode: '+353', format: '## ### ####', maxLength: 9 },
  { code: 'IL', name: 'Israel', dialCode: '+972', format: '## ### ####', maxLength: 9 },
  { code: 'IT', name: 'Italy', dialCode: '+39', format: '### ### ####', maxLength: 10 },
  { code: 'JM', name: 'Jamaica', dialCode: '+1876', format: '### ####', maxLength: 7 },
  { code: 'JP', name: 'Japan', dialCode: '+81', format: '## #### ####', maxLength: 10 },
  { code: 'JO', name: 'Jordan', dialCode: '+962', format: '# #### ####', maxLength: 9 },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '+7', format: '### ### ## ##', maxLength: 10 },
  { code: 'KE', name: 'Kenya', dialCode: '+254', format: '### ######', maxLength: 9 },
  { code: 'KI', name: 'Kiribati', dialCode: '+686', format: '## ###', maxLength: 5 },
  { code: 'KP', name: 'North Korea', dialCode: '+850', format: '### ### ####', maxLength: 10 },
  { code: 'KR', name: 'South Korea', dialCode: '+82', format: '## #### ####', maxLength: 10 },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', format: '#### ####', maxLength: 8 },
  { code: 'KG', name: 'Kyrgyzstan', dialCode: '+996', format: '### ### ###', maxLength: 9 },
  { code: 'LA', name: 'Laos', dialCode: '+856', format: '## ### ###', maxLength: 8 },
  { code: 'LV', name: 'Latvia', dialCode: '+371', format: '## ### ###', maxLength: 8 },
  { code: 'LB', name: 'Lebanon', dialCode: '+961', format: '## ### ###', maxLength: 8 },
  { code: 'LS', name: 'Lesotho', dialCode: '+266', format: '## ### ###', maxLength: 8 },
  { code: 'LR', name: 'Liberia', dialCode: '+231', format: '## ### ####', maxLength: 9 },
  { code: 'LY', name: 'Libya', dialCode: '+218', format: '## ### ####', maxLength: 9 },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423', format: '### ### ###', maxLength: 9 },
  { code: 'LT', name: 'Lithuania', dialCode: '+370', format: '### ## ###', maxLength: 8 },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352', format: '### ### ###', maxLength: 9 },
  { code: 'MO', name: 'Macau', dialCode: '+853', format: '#### ####', maxLength: 8 },
  { code: 'MK', name: 'North Macedonia', dialCode: '+389', format: '## ### ###', maxLength: 8 },
  { code: 'MG', name: 'Madagascar', dialCode: '+261', format: '## ## ### ##', maxLength: 9 },
  { code: 'MW', name: 'Malawi', dialCode: '+265', format: '## ### ####', maxLength: 9 },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', format: '## ### ####', maxLength: 9 },
  { code: 'MV', name: 'Maldives', dialCode: '+960', format: '### ####', maxLength: 7 },
  { code: 'ML', name: 'Mali', dialCode: '+223', format: '## ## ## ##', maxLength: 8 },
  { code: 'MT', name: 'Malta', dialCode: '+356', format: '#### ####', maxLength: 8 },
  { code: 'MH', name: 'Marshall Islands', dialCode: '+692', format: '### ####', maxLength: 7 },
  { code: 'MR', name: 'Mauritania', dialCode: '+222', format: '## ## ## ##', maxLength: 8 },
  { code: 'MU', name: 'Mauritius', dialCode: '+230', format: '#### ####', maxLength: 8 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', format: '## #### ####', maxLength: 10 },
  { code: 'FM', name: 'Micronesia', dialCode: '+691', format: '### ####', maxLength: 7 },
  { code: 'MD', name: 'Moldova', dialCode: '+373', format: '## ### ###', maxLength: 8 },
  { code: 'MC', name: 'Monaco', dialCode: '+377', format: '## ## ## ##', maxLength: 8 },
  { code: 'MN', name: 'Mongolia', dialCode: '+976', format: '## ## ####', maxLength: 8 },
  { code: 'ME', name: 'Montenegro', dialCode: '+382', format: '## ### ###', maxLength: 8 },
  { code: 'MA', name: 'Morocco', dialCode: '+212', format: '## ### ####', maxLength: 9 },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258', format: '## ### ####', maxLength: 9 },
  { code: 'MM', name: 'Myanmar', dialCode: '+95', format: '## ### ####', maxLength: 9 },
  { code: 'NA', name: 'Namibia', dialCode: '+264', format: '## ### ####', maxLength: 9 },
  { code: 'NR', name: 'Nauru', dialCode: '+674', format: '### ####', maxLength: 7 },
  { code: 'NP', name: 'Nepal', dialCode: '+977', format: '## #### ####', maxLength: 10 },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', format: '# ## ## ## ##', maxLength: 9 },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', format: '## ### ####', maxLength: 9 },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505', format: '#### ####', maxLength: 8 },
  { code: 'NE', name: 'Niger', dialCode: '+227', format: '## ## ## ##', maxLength: 8 },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', format: '### ### ####', maxLength: 10 },
  { code: 'NO', name: 'Norway', dialCode: '+47', format: '### ## ###', maxLength: 8 },
  { code: 'OM', name: 'Oman', dialCode: '+968', format: '#### ####', maxLength: 8 },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', format: '### #######', maxLength: 10 },
  { code: 'PW', name: 'Palau', dialCode: '+680', format: '### ####', maxLength: 7 },
  { code: 'PS', name: 'Palestine', dialCode: '+970', format: '### ### ###', maxLength: 9 },
  { code: 'PA', name: 'Panama', dialCode: '+507', format: '#### ####', maxLength: 8 },
  { code: 'PG', name: 'Papua New Guinea', dialCode: '+675', format: '### ####', maxLength: 7 },
  { code: 'PY', name: 'Paraguay', dialCode: '+595', format: '### ### ###', maxLength: 9 },
  { code: 'PE', name: 'Peru', dialCode: '+51', format: '### ### ###', maxLength: 9 },
  { code: 'PH', name: 'Philippines', dialCode: '+63', format: '### ### ####', maxLength: 10 },
  { code: 'PL', name: 'Poland', dialCode: '+48', format: '### ### ###', maxLength: 9 },
  { code: 'PT', name: 'Portugal', dialCode: '+351', format: '### ### ###', maxLength: 9 },
  { code: 'PR', name: 'Puerto Rico', dialCode: '+1787', format: '### ####', maxLength: 7 },
  { code: 'QA', name: 'Qatar', dialCode: '+974', format: '#### ####', maxLength: 8 },
  { code: 'RO', name: 'Romania', dialCode: '+40', format: '### ### ###', maxLength: 9 },
  { code: 'RU', name: 'Russia', dialCode: '+7', format: '### ### ## ##', maxLength: 10 },
  { code: 'RW', name: 'Rwanda', dialCode: '+250', format: '### ### ###', maxLength: 9 },
  { code: 'KN', name: 'Saint Kitts and Nevis', dialCode: '+1869', format: '### ####', maxLength: 7 },
  { code: 'LC', name: 'Saint Lucia', dialCode: '+1758', format: '### ####', maxLength: 7 },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', dialCode: '+1784', format: '### ####', maxLength: 7 },
  { code: 'WS', name: 'Samoa', dialCode: '+685', format: '## ####', maxLength: 6 },
  { code: 'SM', name: 'San Marino', dialCode: '+378', format: '## ## ## ##', maxLength: 8 },
  { code: 'ST', name: 'Sao Tome and Principe', dialCode: '+239', format: '### ####', maxLength: 7 },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', format: '## ### ####', maxLength: 9 },
  { code: 'SN', name: 'Senegal', dialCode: '+221', format: '## ### ## ##', maxLength: 9 },
  { code: 'RS', name: 'Serbia', dialCode: '+381', format: '## ### ####', maxLength: 9 },
  { code: 'SC', name: 'Seychelles', dialCode: '+248', format: '# ### ###', maxLength: 7 },
  { code: 'SL', name: 'Sierra Leone', dialCode: '+232', format: '## ### ###', maxLength: 8 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', format: '#### ####', maxLength: 8 },
  { code: 'SK', name: 'Slovakia', dialCode: '+421', format: '### ### ###', maxLength: 9 },
  { code: 'SI', name: 'Slovenia', dialCode: '+386', format: '## ### ###', maxLength: 8 },
  { code: 'SB', name: 'Solomon Islands', dialCode: '+677', format: '## ###', maxLength: 5 },
  { code: 'SO', name: 'Somalia', dialCode: '+252', format: '## ### ###', maxLength: 8 },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', format: '## ### ####', maxLength: 9 },
  { code: 'SS', name: 'South Sudan', dialCode: '+211', format: '### ### ###', maxLength: 9 },
  { code: 'ES', name: 'Spain', dialCode: '+34', format: '### ### ###', maxLength: 9 },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', format: '## ### ####', maxLength: 9 },
  { code: 'SD', name: 'Sudan', dialCode: '+249', format: '## ### ####', maxLength: 9 },
  { code: 'SR', name: 'Suriname', dialCode: '+597', format: '### ####', maxLength: 7 },
  { code: 'SE', name: 'Sweden', dialCode: '+46', format: '## ### ## ##', maxLength: 9 },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', format: '## ### ## ##', maxLength: 9 },
  { code: 'SY', name: 'Syria', dialCode: '+963', format: '### ### ###', maxLength: 9 },
  { code: 'TW', name: 'Taiwan', dialCode: '+886', format: '### ### ###', maxLength: 9 },
  { code: 'TJ', name: 'Tajikistan', dialCode: '+992', format: '## ### ####', maxLength: 9 },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255', format: '### ### ###', maxLength: 9 },
  { code: 'TH', name: 'Thailand', dialCode: '+66', format: '## ### ####', maxLength: 9 },
  { code: 'TL', name: 'Timor-Leste', dialCode: '+670', format: '### ####', maxLength: 7 },
  { code: 'TG', name: 'Togo', dialCode: '+228', format: '## ### ###', maxLength: 8 },
  { code: 'TO', name: 'Tonga', dialCode: '+676', format: '## ###', maxLength: 5 },
  { code: 'TT', name: 'Trinidad and Tobago', dialCode: '+1868', format: '### ####', maxLength: 7 },
  { code: 'TN', name: 'Tunisia', dialCode: '+216', format: '## ### ###', maxLength: 8 },
  { code: 'TR', name: 'Turkey', dialCode: '+90', format: '### ### ## ##', maxLength: 10 },
  { code: 'TM', name: 'Turkmenistan', dialCode: '+993', format: '## ## ## ##', maxLength: 8 },
  { code: 'TV', name: 'Tuvalu', dialCode: '+688', format: '## ###', maxLength: 5 },
  { code: 'UG', name: 'Uganda', dialCode: '+256', format: '### ### ###', maxLength: 9 },
  { code: 'UA', name: 'Ukraine', dialCode: '+380', format: '## ### ## ##', maxLength: 9 },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', format: '## ### ####', maxLength: 9 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', format: '#### ######', maxLength: 10 },
  { code: 'US', name: 'United States', dialCode: '+1', format: '### ### ####', maxLength: 10 },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', format: '## ### ###', maxLength: 8 },
  { code: 'UZ', name: 'Uzbekistan', dialCode: '+998', format: '## ### ## ##', maxLength: 9 },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678', format: '## ####', maxLength: 6 },
  { code: 'VA', name: 'Vatican City', dialCode: '+379', format: '### ### ####', maxLength: 10 },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', format: '### ### ####', maxLength: 10 },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', format: '### ### ###', maxLength: 9 },
  { code: 'YE', name: 'Yemen', dialCode: '+967', format: '### ### ###', maxLength: 9 },
  { code: 'ZM', name: 'Zambia', dialCode: '+260', format: '## ### ####', maxLength: 9 },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263', format: '## ### ####', maxLength: 9 },
];

const ProfileSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: '',
    timezone: 'UTC',
  });
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries.find(c => c.code === 'US') || countries[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [locationRequested, setLocationRequested] = useState(false);
  const [preferredCurrency, setPreferredCurrency] = useState<Currency>('INR');

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    const query = searchQuery.toLowerCase();
    return countries.filter(
      c => c.name.toLowerCase().includes(query) ||
           c.dialCode.includes(query) ||
           c.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Detect country from browser location
  useEffect(() => {
    if (locationRequested) return;
    setLocationRequested(true);

    // Try to get country from browser's geolocation API via timezone
    const detectCountryFromTimezone = () => {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneToCountry: Record<string, string> = {
          'Asia/Kolkata': 'IN',
          'Asia/Calcutta': 'IN',
          'America/New_York': 'US',
          'America/Los_Angeles': 'US',
          'America/Chicago': 'US',
          'America/Denver': 'US',
          'Europe/London': 'GB',
          'Europe/Paris': 'FR',
          'Europe/Berlin': 'DE',
          'Asia/Tokyo': 'JP',
          'Asia/Shanghai': 'CN',
          'Asia/Singapore': 'SG',
          'Australia/Sydney': 'AU',
          'America/Toronto': 'CA',
          'America/Sao_Paulo': 'BR',
          'Asia/Dubai': 'AE',
          'Europe/Moscow': 'RU',
        };

        const countryCode = timezoneToCountry[timezone];
        if (countryCode) {
          const country = countries.find(c => c.code === countryCode);
          if (country) {
            setSelectedCountry(country);
          }
        }
      } catch {
        // Fallback to default
      }
    };

    detectCountryFromTimezone();
  }, [locationRequested]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch profile data - gracefully handle if API not available
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        const response = await profileService.get();
        return response.data as ProfileData;
      } catch {
        // Return user data from auth store as fallback
        return {
          id: user?.id || '',
          email: user?.email || '',
          name: user?.name || '',
          phone: '',
          timezone: 'UTC',
          profile_photo_url: undefined,
          created_at: '',
          updated_at: '',
        } as ProfileData;
      }
    },
    retry: false,
  });

  // Fetch appearance preferences for currency
  const { data: appearancePrefs } = useQuery({
    queryKey: ['appearancePreferences'],
    queryFn: async () => {
      try {
        const response = await preferencesService.getAppearance();
        return response.data;
      } catch {
        return { preferred_currency: 'INR' };
      }
    },
    retry: false,
  });

  // Update currency when preferences are fetched
  useEffect(() => {
    if (appearancePrefs?.preferred_currency) {
      setPreferredCurrency(appearancePrefs.preferred_currency as Currency);
    }
  }, [appearancePrefs]);

  // Update form data when profile is fetched
  useEffect(() => {
    if (profile) {
      // Parse existing phone number to extract country code and number
      let phoneNumber = profile.phone || '';
      let detectedCountry = selectedCountry;

      if (phoneNumber) {
        // Try to match country code from phone number
        for (const country of countries) {
          if (phoneNumber.startsWith(country.dialCode)) {
            detectedCountry = country;
            phoneNumber = phoneNumber.slice(country.dialCode.length).trim();
            break;
          }
        }
      }

      setSelectedCountry(detectedCountry);
      setFormData({
        name: profile.name || '',
        phone: phoneNumber,
        timezone: profile.timezone || 'UTC',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Validate phone number
  const validatePhone = (phone: string, country: Country): string => {
    if (!phone) return ''; // Empty is valid (optional field)

    const digitsOnly = phone.replace(/\D/g, '');

    if (digitsOnly.length < 6) {
      return 'Phone number is too short';
    }

    if (digitsOnly.length > country.maxLength) {
      return `Phone number should be ${country.maxLength} digits for ${country.name}`;
    }

    return '';
  };

  // Handle phone input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d\s\-()]/g, ''); // Allow digits, spaces, dashes, parentheses
    setFormData({ ...formData, phone: value });
    setPhoneError(validatePhone(value, selectedCountry));
  };

  // Handle country selection
  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setPhoneError(validatePhone(formData.phone, country));
  };

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; phone?: string; timezone?: string }) =>
      profileService.update(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Update auth store with new name
      if (response.data.name) {
        updateUser({ name: response.data.name });
      }
      toast.success('Profile updated successfully');
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  // Upload photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: (file: File) => profileService.uploadPhoto(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Profile photo uploaded successfully');
    },
    onError: () => {
      toast.error('Failed to upload photo');
    },
  });

  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: () => profileService.deletePhoto(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Profile photo removed');
    },
    onError: () => {
      toast.error('Failed to remove photo');
    },
  });

  // Update currency preference mutation
  const updateCurrencyMutation = useMutation({
    mutationFn: (currency: Currency) =>
      preferencesService.updateAppearance({ preferred_currency: currency }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appearancePreferences'] });
    },
    onError: () => {
      toast.error('Failed to update currency preference');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Skip phone validation and update if phone is KYC-verified
    const isPhoneVerified = profile?.phone_verified;

    if (!isPhoneVerified) {
      const error = validatePhone(formData.phone, selectedCountry);
      if (error) {
        setPhoneError(error);
        toast.error(error);
        return;
      }
    }

    // Format phone with country code
    const fullPhone = formData.phone
      ? `${selectedCountry.dialCode} ${formData.phone.trim()}`
      : '';

    // Update profile - exclude phone if KYC-verified
    updateMutation.mutate({
      name: formData.name,
      timezone: formData.timezone,
      ...(isPhoneVerified ? {} : { phone: fullPhone }),
    });

    // Update currency preference if changed
    if (preferredCurrency !== appearancePrefs?.preferred_currency) {
      updateCurrencyMutation.mutate(preferredCurrency);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPG, PNG, GIF, WebP');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }

    uploadPhotoMutation.mutate(file);
  };

  const handleRemovePhoto = () => {
    deletePhotoMutation.mutate();
  };

  const resetForm = () => {
    if (!profile) return;
    let phoneNumber = profile.phone || '';
    for (const country of countries) {
      if (phoneNumber.startsWith(country.dialCode)) {
        phoneNumber = phoneNumber.slice(country.dialCode.length).trim();
        break;
      }
    }
    setFormData({ name: profile.name || '', phone: phoneNumber, timezone: profile.timezone || 'UTC' });
    setPhoneError('');
    setPreferredCurrency((appearancePrefs?.preferred_currency as Currency) || 'INR');
  };

  if (isLoadingProfile) {
    return <Skeleton className="h-[420px] rounded-[14px]" />;
  }

  const displayName = formData.name || profile?.email || user?.email || 'U';
  const saving = updateMutation.isPending || updateCurrencyMutation.isPending;

  return (
    <section className="ps-card p-5 sm:p-6 flex flex-col gap-5" aria-labelledby="profile-heading">
      <div>
        <h2 id="profile-heading" className="m-0 text-lg font-medium text-ps-text">Profile</h2>
        <p className="m-0 mt-1 text-ps-muted text-sm">Your personal details and display preferences.</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Photo */}
        <div className="flex flex-wrap items-center gap-4">
          <span className="w-16 h-16 rounded-full bg-ps-accent-soft text-ps-accent grid place-items-center text-2xl font-semibold overflow-hidden flex-none">
            {profile?.profile_photo_url ? (
              <img src={profile.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              displayName[0].toUpperCase()
            )}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadPhotoMutation.isPending}>
              {uploadPhotoMutation.isPending ? 'Uploading…' : 'Change photo'}
            </Button>
            {profile?.profile_photo_url && (
              <Button variant="secondary" onClick={handleRemovePhoto} disabled={deletePhotoMutation.isPending}>
                {deletePhotoMutation.isPending ? 'Removing…' : 'Remove'}
              </Button>
            )}
          </div>
          <span className="text-ps-subtle text-xs">JPG, PNG, GIF or WebP, up to 2 MB</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handlePhotoSelect}
          />
        </div>

        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,260px),1fr))]">
          <div>
            <label className="ps-label" htmlFor="pf-name">Full name</label>
            <input
              id="pf-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="ps-input"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="ps-label" htmlFor="pf-email">Email</label>
            <input id="pf-email" type="email" value={profile?.email || user?.email || ''} className="ps-input opacity-70 cursor-not-allowed" readOnly />
          </div>

          <div>
            <label className="ps-label flex items-center gap-2" htmlFor="pf-phone">
              Phone
              {profile?.phone_verified && <Tag tone="ok">Verified via KYC</Tag>}
            </label>
            {profile?.phone_verified ? (
              <input id="pf-phone" type="tel" value={profile.phone || ''} className="ps-input opacity-70 cursor-not-allowed" readOnly />
            ) : (
              <>
                <div className="flex gap-2">
                  {/* Country code */}
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="ps-input w-auto flex items-center gap-1.5 whitespace-nowrap"
                      aria-label={`Country code ${selectedCountry.name} ${selectedCountry.dialCode}`}
                      aria-expanded={isDropdownOpen}
                    >
                      <span>{selectedCountry.code}</span>
                      <span className="text-ps-muted">{selectedCountry.dialCode}</span>
                      <ChevronDownIcon className={`w-4 h-4 text-ps-muted transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-72 max-w-[80vw] ps-card shadow-xl overflow-hidden">
                        <div className="p-2 border-b border-ps-border relative">
                          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ps-subtle" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search countries"
                            className="ps-input pl-8"
                            autoFocus
                          />
                        </div>
                        <div className="overflow-y-auto max-h-48">
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => handleCountrySelect(country)}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-ps-hover ${
                                  selectedCountry.code === country.code ? 'bg-ps-raised' : ''
                                }`}
                              >
                                <span className="w-8 text-ps-muted">{country.code}</span>
                                <span className="flex-1 truncate text-ps-text">{country.name}</span>
                                <span className="text-ps-subtle">{country.dialCode}</span>
                              </button>
                            ))
                          ) : (
                            <p className="m-0 px-3 py-4 text-ps-muted text-center text-sm">No countries found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <input
                    id="pf-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className={`ps-input flex-1 ${phoneError ? '!border-ps-err' : ''}`}
                    placeholder={selectedCountry.format.replace(/#/g, '0')}
                    maxLength={selectedCountry.maxLength + 5}
                    aria-invalid={!!phoneError}
                  />
                </div>
                <p className={`m-0 mt-1.5 text-xs ${phoneError ? 'text-ps-err' : 'text-ps-subtle'}`}>
                  {phoneError || `Format: ${selectedCountry.dialCode} ${selectedCountry.format.replace(/#/g, 'X')}`}
                </p>
              </>
            )}
          </div>

          <div>
            <label className="ps-label" htmlFor="pf-tz">Timezone</label>
            <select
              id="pf-tz"
              className="ps-input"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            >
              <option value="UTC">UTC (Coordinated Universal Time)</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Kolkata">India Standard Time (IST)</option>
              <option value="Asia/Singapore">Singapore Time (SGT)</option>
              <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
              <option value="Australia/Sydney">Sydney (AEDT)</option>
            </select>
          </div>
        </div>

        <p className="-mt-1.5 m-0 text-ps-subtle text-xs">
          Email{profile?.phone_verified ? ' and KYC phone' : ''} can only be changed through{' '}
          <a href="mailto:support@podstack.ai" className="ps-link">support</a>.
        </p>

        <div>
          <span className="ps-label" id="pf-currency">Show amounts in</span>
          <div className="flex gap-2.5 max-w-[360px]" role="radiogroup" aria-labelledby="pf-currency">
            {(['INR', 'USD'] as Currency[]).map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={preferredCurrency === c}
                onClick={() => setPreferredCurrency(c)}
                className={`flex-1 ${preferredCurrency === c ? 'ps-chip ps-chip-on' : 'ps-chip'} justify-center min-h-[44px] rounded-md`}
              >
                {c === 'INR' ? '₹ INR' : '$ USD'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2.5 border-t border-ps-border pt-4">
          <Button variant="secondary" onClick={resetForm} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || !!phoneError}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </section>
  );
};

export default ProfileSettings;
